import React, { useEffect, useState, useRef } from "react";
import { Grid, Button, Drawer, TextField } from "@material-ui/core";
import BoxWrapper from "../../../../../components/ui/boxWrapper";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";
import axios from "../../../../../axios-order";
import Card from "../../../../../components/ui/Cards";
import VendorCreditNote from "../../../../../asserts/templates/vendor_credit_note.xlsx";
import AddCreditNoteForm from "./addCreditNote";
import EditForm from "./editForm";
import ShowCreditForm from "./showCreditNote";
import VisibilityIcon from "@material-ui/icons/Visibility";
import "./CreditNote.css";
import { ValidatorForm } from "react-material-ui-form-validator";
import moment from "moment";
import { TablePagination } from "@material-ui/core";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { canWrite } from '../../../../../utils/privilegeUtils'
import ToggleSwitch from "../../../../../components/ui/ToggleSwitch/ToggleSwitch";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import RightDrawer from "../../../../../components/ui/RightDrawer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { withRouter } from 'react-router-dom';
import Modal from "react-bootstrap/Modal";
import { FormControl, FormControlLabel, RadioGroup, Radio } from '@material-ui/core';
import DatePicker from '../../../../../components/ui/DatePicker'

// Added for the in-grid column filter row
import ClearIcon from "@material-ui/icons/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Chip from "@material-ui/core/Chip";


// Set to true AFTER BoxWrapper has been given a `headerExtra` prop (render
// {props.headerExtra} right after its search input). The Filter button then
// appears next to the Credit Note Details search. While false, the button is
// rendered just above the table instead, so it never disappears.
const FILTER_BUTTON_IN_BOXWRAPPER = true;

// Indian-style number formatting for the money columns in the Credit Note grid.
const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// ---------------------------------------------------------------------
// COLUMNS for the Credit Note Details grid — one entry per key returned by
// /get_vendor_credit_details. `group` drives the colored group-band row,
// `width` (px) gives every header / filter box / cell a fixed size so the
// sticky header, filter row and data all line up exactly; the table scrolls
// horizontally instead of squeezing columns. An optional `render` formats
// the cell value (e.g. amounts).
// IMPORTANT: columns that share a group must stay next to each other in
// these arrays, because the group band is built from consecutive columns.
// ---------------------------------------------------------------------
const filterableColumns = [
  // Vendor
  { key: "vendorName", label: "Vendor Name", group: "Vendor", width: 230 },
  { key: "erpVendorCode", label: "ERP Vendor Code", group: "Vendor", width: 150 },
  { key: "vendorCode", label: "Vendor Code", group: "Vendor", width: 120 },
  { key: "vendorGstin", label: "GSTIN", group: "Vendor", width: 170 },
  { key: "vendorPan", label: "PAN", group: "Vendor", width: 130 },

  // Original Bill
  { key: "originalBillNumber", label: "Bill Number", group: "Original Bill", width: 180 },
  { key: "originalBillDate", label: "Bill Date", group: "Original Bill", width: 130 },

  // Credit Note
  { key: "creditNoteNumber", label: "Credit Note Number", group: "Credit Note", width: 190 },
  { key: "creditNoteDate", label: "Credit Note Date", group: "Credit Note", width: 140 },
  { key: "bookingDate", label: "Booking Date", group: "Credit Note", width: 130 },
  { key: "creditNoteReason", label: "Credit Note Reason", group: "Credit Note", width: 220 },
  { key: "eInvoice", label: "E-Invoice", group: "Credit Note", width: 260 },
  { key: "expenseType", label: "Expense Type", group: "Credit Note", width: 220 },
  { key: "pos", label: "POS", group: "Credit Note", width: 80 },

  // Tax Details
  { key: "hsnSacCode", label: "HSN/SAC Code", group: "Tax Details", width: 130 },
  { key: "taxCreditType", label: "Tax Credit Type", group: "Tax Details", width: 150 },
  { key: "itcEligibility", label: "ITC Eligibility", group: "Tax Details", width: 130 },
  { key: "rcmApplicability", label: "RCM Applicability", group: "Tax Details", width: 150 },

  // Amounts
  { key: "taxableValue", label: "Taxable Value", group: "Amounts", width: 140, render: (row) => formatAmount(row.taxableValue) },
  { key: "nonTaxableValue", label: "Non Taxable Value", group: "Amounts", width: 150, render: (row) => formatAmount(row.nonTaxableValue) },
  { key: "sgst", label: "SGST", group: "Amounts", width: 110, render: (row) => formatAmount(row.sgst) },
  { key: "cgst", label: "CGST", group: "Amounts", width: 110, render: (row) => formatAmount(row.cgst) },
  { key: "igst", label: "IGST", group: "Amounts", width: 110, render: (row) => formatAmount(row.igst) },
  { key: "cess", label: "CESS", group: "Amounts", width: 100, render: (row) => formatAmount(row.cess) },
  { key: "taxAmount", label: "Tax Amount", group: "Amounts", width: 130, render: (row) => formatAmount(row.taxAmount) },
  { key: "totalAmount", label: "Total Amount", group: "Amounts", width: 140, render: (row) => formatAmount(row.totalAmount) },

  // TCS / TDS
  { key: "tcsSection", label: "TCS Section", group: "TCS / TDS", width: 120 },
  { key: "tcsPayable", label: "TCS Payable", group: "TCS / TDS", width: 130, render: (row) => formatAmount(row.tcsPayable) },
  { key: "tdsSection", label: "TDS Section", group: "TCS / TDS", width: 120 },
  { key: "tdsPayable", label: "TDS Payable", group: "TCS / TDS", width: 130, render: (row) => formatAmount(row.tdsPayable) },

  // Dispute
  { key: "dispute", label: "Dispute", group: "Dispute", width: 110 },
  { key: "disputeValue", label: "Dispute Value", group: "Dispute", width: 140, render: (row) => formatAmount(row.disputeValue) },

  // Bill of Entry
  { key: "boeNumber", label: "BOE Number", group: "Bill of Entry", width: 130 },
  { key: "boeDate", label: "BOE Date", group: "Bill of Entry", width: 120 },
  { key: "referenceDate", label: "Reference Date", group: "Bill of Entry", width: 140 },
  { key: "portCode", label: "Port Code", group: "Bill of Entry", width: 110 },
];

// Trailing, display-only columns (not filterable) — still part of the
// grouped header so the colored bands cover the full table width.
const nonFilterableColumns = [
  { key: "createDate", label: "Create Date", group: "Record Info", width: 170 },
  { key: "modifyDate", label: "Modify Date", group: "Record Info", width: 170 },
  { key: "created_date", label: "Created Date (System)", group: "Record Info", width: 190 },
  { key: "modified_date", label: "Modified Date (System)", group: "Record Info", width: 190 },
  { key: "inv_date", label: "Invoice Date (System)", group: "Record Info", width: 190 },
];

// Every column, filterable or not.
const allColumns = [...filterableColumns, ...nonFilterableColumns];

// Fixed width (px) for the trailing sticky "SHOW" column.
const SHOW_COL_WIDTH = 70;

// Fixed heights (px) of the two sticky header rows. The label row and the
// filter row use these to compute their sticky `top` offsets, so the three
// rows stack exactly with no gap or overlap.
const GROUP_ROW_HEIGHT = 30;
const LABEL_ROW_HEIGHT = 32;

// Deep teal accent — same color used throughout the filter UI.
const ACCENT = "#0f766e";

// Header colours — subtle tints of the app's navy (#03406f) on neutral greys.
const HEADER_GROUP_BG = "#e6ecf2";
const HEADER_LABEL_BG = "#f5f7f9";
const HEADER_TEXT = "#03406f";
const HEADER_BORDER = "#d3dce5";

// Filter panel sections. Basic filters are open by default; every other
// filterable column goes into the collapsed Advanced section.
const BASIC_FILTER_KEYS = [
  "vendorName",
  "erpVendorCode",
  "vendorCode",
  "vendorGstin",
  "vendorPan",
  "originalBillNumber",
  "originalBillDate",
  "creditNoteNumber",
  "creditNoteDate",
];

// Nothing to exclude on this screen.
// Filter-panel columns that take a date: they use the shared DatePicker.
const FILTER_DATE_KEYS = ["originalBillDate", "creditNoteDate", "bookingDate", "dueDate", "boeDate", "referenceDate"];

// Dates are filtered as YYYY-MM-DD (what the calendar gives); chips show DD-MM-YYYY.
const toDisplayDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
};

const FILTER_EXCLUDED_KEYS = [];

// Columns shown as a dropdown in the filter panel. Yes/No-style columns
// (E-Invoice, ITC Eligibility, RCM Applicability, Dispute) are dropdowns.
// TDS / TCS Section have many values, so they stay free-text (comma lists).
const FILTER_DROPDOWN_OPTIONS = {
  eInvoice: ["Yes", "No"],
  itcEligibility: ["Yes", "No"],
  rcmApplicability: ["Yes", "No"],
  dispute: ["Yes", "No"],
  taxCreditType: ["Goods", "Service", "Capital Goods"],
};

const getBasicFilterColumns = () =>
  BASIC_FILTER_KEYS.map((k) => filterableColumns.find((c) => c.key === k)).filter(Boolean);

const getAdvancedFilterColumns = () =>
  filterableColumns.filter(
    (c) => !BASIC_FILTER_KEYS.includes(c.key) && !FILTER_EXCLUDED_KEYS.includes(c.key)
  );

const getFieldMeta = (fieldKey) =>
  filterableColumns.find((c) => c.key === fieldKey) || filterableColumns[0];

// Color band per group.
const GROUP_COLORS = {
  "Vendor": "#0f766e",
  "Original Bill": "#0e7490",
  "Credit Note": "#0369a1",
  "Tax Details": "#7c3aed",
  "Amounts": "#b45309",
  "TCS / TDS": "#be185d",
  "Dispute": "#b91c1c",
  "Bill of Entry": "#4d7c0f",
  "Record Info": "#475569",
};

// Distinct groups, in first-seen order, with how many columns each spans
// — drives the top header row's colSpan per group band.
const getColumnGroups = () => {
  const groups = [];
  allColumns.forEach((col) => {
    const existing = groups.find((g) => g.name === col.group);
    if (existing) {
      existing.span += 1;
    } else {
      groups.push({ name: col.group, span: 1 });
    }
  });
  return groups;
};

// Total pixel width of every column belonging to a given group — used to
// size the group-band <th> so it lines up exactly with the columns below it.
const getGroupWidth = (groupName) =>
  allColumns
    .filter((c) => c.group === groupName)
    .reduce((sum, c) => sum + c.width, 0);

// Sum of every column's width plus the sticky SHOW column — used as the
// table's min-width so the horizontal scrollbar kicks in instead of the
// browser squeezing columns to fit.
const getTableMinWidth = () =>
  allColumns.reduce((sum, c) => sum + c.width, SHOW_COL_WIDTH);

// Splits a raw string on commas into trimmed, non-empty, de-duplicated
// values. Used for every field's value box so a person can type or paste
// "abc ltd, mr.gg, amazon" into one field and match any of them.
// The green in-table filter row (one box per column) is switched off; the
// Filter panel is the way to filter. Set to true to bring the row back.
const SHOW_INLINE_FILTER_ROW = false;

const parseBulkFilterValues = (raw) => {
  return Array.from(
    new Set(
      (raw || "")
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
    )
  );
};

function InvoiceMgmt(props) {
  const onCickOperation = true;
  const [tableData, setTableData] = useState([]);

  const [editData, setEditData] = useState({});
  const [showData, setShowData] = useState({});

  const [openAddFormDrawer, setOpenAddFormDrawer] = useState(false);
  const [openShowDrawer, setOpenShowDrawer] = useState(false);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);

  const [uploadSection, setUploadSection] = useState(false);
  const [uploadResData, setUploadResData] = useState();
  const [uploadErrorMsg, setUploadErrorMsg] = useState();

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  // true while Export All / Export Filtered is running (its button spinner)
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [tableSearch, setTableSearch] = useState("");

  const [stateForPage, setStateForPage] = useState(false);
  const [stateForRowPerPage, setStateForRowPerPage] = useState(false);
  const [stateTableSearch, setStateTableSearch] = useState(false);

  const [uploadHistoryTableData, setUploadHistoryTableData] = useState([]);
  // const [showUploadHistoryTable, setShowUploadHistoryTable] = useState(false);
  const [loading, setLoading] = useState(true);

  const [dateFilterOption, setDateFilterOption] = useState("withDateFilter")
  const [triggerModelFromToDate, setTriggerModelFromToDate] = useState(false)
  const [fromPeriodDisplay, setFromPeriodDisplay] = useState(null)
  const [fromPeriod, setFromPeriod] = useState(null)
  const [toPeriodDisplay, setToPeriodDisplay] = useState('')
  const [toPeriod, setToPeriod] = useState(null)
  const { errorPage, perErrorPages, errorTotal } = {
    errorPage: 1, // Current page
    perErrorPages: 10, // Records per page
    errorTotal: 0, // Total records
  };

  const [currentErrorPage, setCurrentErrorPage] = useState(errorPage);
  const [errorRowsPerPage, setErrorRowsPerPage] = useState(perErrorPages);

  const [loadingExport, setLoadingExport] = useState(false);

  // ---------------------------------------------------------------
  // IN-GRID MULTI-FIELD FILTER (same pattern as Collections / Payment
  // Advice / Vendor / Bill / Debit Note Management): a dedicated filter
  // row sits directly under the sticky grouped header, one compact box per
  // column. Each box takes a comma-separated "is any of" list, and every
  // typed box is combined (AND/OR) into a SINGLE apply.
  // ---------------------------------------------------------------
  // inlineFilterValues: what's currently typed into each column's box,
  // keyed by field — draft state, not yet sent to the backend.
  const [inlineFilterValues, setInlineFilterValues] = useState({});
  // appliedFilters: the conditions actually in effect on the table.
  //   Each condition: { field, value } — value is the raw comma string.
  const [appliedFilters, setAppliedFilters] = useState([]);
  // filterMatchType: "AND" (every filled column must match) or "OR"
  // (any filled column matching is enough).
  const [filterMatchType, setFilterMatchType] = useState("AND");

  // ---------------------------------------------------------------
  // COMMON FILTER PANEL — one "Filter" button opens a compact side panel
  // next to the table (Basic filters open, Advanced collapsed). "Apply
  // Filters" commits everything in a SINGLE api call through the same
  // appliedFilters / applyDynamicFilters flow used above.
  // ---------------------------------------------------------------
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  // draftValues: what is typed in the panel, keyed by column key.
  const [draftValues, setDraftValues] = useState({});

  const getCreditNoteDetails = (
    serachIn,
    pageParam = page,
    rowsPerPageParam = rowsPerPage
  ) => {
    const finalData = {
      user_id: localStorage.getItem("user_id"),
      number_of_rows_per_page: rowsPerPageParam,
      page_number: pageParam,
      search_text: serachIn,
      business_entity_id: localStorage.getItem("business_entity_id"),
    };
    axios
      .post(`/get_vendor_credit_details`, finalData)
      .then((res) => {
        setTableData(res?.data?.record);
        setRowCount(res?.data?.row_count);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  };

  // Filter API call — takes the { match_type, conditions } payload built by
  // applyDynamicFilters and posts it ONCE for all active conditions, along
  // with pagination. If your backend route differs, just change the URL below.
  const getCreditNoteFilterApi = (filters = {}, pageParam = page, rowsPerPageParam = rowsPerPage) => {
    setLoading(true);
    axios
      .post(`/get_vendor_credit_detailsFilter`, {   // ← change URL if your route is different
        business_entity_id: localStorage.getItem("business_entity_id"),
        search_text: tableSearch || "",
        filters: filters,
        number_of_rows_per_page: rowsPerPageParam,
        page_number: pageParam,
      })
      .then((res) => {
        setTableData(res?.data?.record || res?.data?.records || []);
        setRowCount(res?.data?.row_count || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        toast.error("Failed to fetch filtered data");
      });
  };


  useEffect(() => {
    if (stateForRowPerPage) {
      if (appliedFilters.length > 0) {
        applyDynamicFilters(appliedFilters, filterMatchType, page, rowsPerPage);
      } else {
        getCreditNoteDetails(tableSearch);
      }
    }
    setStateForRowPerPage(false);
  }, [rowsPerPage]);

  useEffect(() => {
    if (stateForPage) {
      if (appliedFilters.length > 0) {
        applyDynamicFilters(appliedFilters, filterMatchType, page, rowsPerPage);
      } else {
        getCreditNoteDetails(tableSearch);
      }
    }
    setStateForPage(false);
  }, [page]);

  // -----------------------------------------------------------------------
  // IN-GRID MULTI-FIELD FILTER
  // -----------------------------------------------------------------------
  // Every filterable column has its own compact box directly in the filter
  // row under the table header. Typing into several boxes at once stages
  // several conditions; nothing is sent to the backend until "Apply" (or
  // Enter) fires ONCE for the whole set.

  // Patches one column's staged (not-yet-applied) value.
  const handleInlineFilterChange = (field, rawValue) => {
    setInlineFilterValues((prev) => ({ ...prev, [field]: rawValue }));
  };

  const getInlineFilterValue = (field) => inlineFilterValues[field] ?? "";

  // Clears just one column's box (its little "×" adornment), without
  // touching any other staged column or applying anything.
  const clearInlineFilterField = (field) => {
    setInlineFilterValues((prev) => ({ ...prev, [field]: "" }));
  };

  // True once at least one column box has something typed into it that
  // hasn't been applied yet — drives the "Apply Filters" affordance.
  const hasStagedFilterChanges = () => {
    const appliedByField = Object.fromEntries(
      appliedFilters.map((c) => [c.field, c.value])
    );
    return filterableColumns.some((col) => {
      const staged = (inlineFilterValues[col.key] ?? "").trim();
      const applied = (appliedByField[col.key] ?? "").trim();
      return staged !== applied;
    });
  };

  const stagedFilterFieldCount = () =>
    Object.values(inlineFilterValues).filter(
      (v) => (v ?? "").toString().trim() !== ""
    ).length;

  // Builds the backend payload from the current conditions and either calls
  // the filter API (when conditions exist) or falls back to the normal
  // paginated listing (when the filter set is empty). Every condition is
  // sent as operator "in" — "field value is any of these".
  const applyDynamicFilters = (
    conditions = appliedFilters,
    matchType = filterMatchType,
    pageParam = 0,
    rowsPerPageParam = rowsPerPage
  ) => {
    if (!conditions || conditions.length === 0) {
      getCreditNoteDetails(tableSearch, pageParam, rowsPerPageParam);
      return;
    }

    const payload = conditions.map((c) => ({
      field: c.field,
      operator: "in",
      value: parseBulkFilterValues(c.value),
    }));

    const filters = { match_type: matchType, conditions: payload };
    getCreditNoteFilterApi(filters, pageParam, rowsPerPageParam);
  };

  // Single action: reads every column's box, keeps only the ones with a
  // value, and fires ONE api call for the combined set.
  const applyInlineFilters = () => {
    const conditions = filterableColumns
      .map((col) => ({
        field: col.key,
        value: (inlineFilterValues[col.key] ?? "").trim(),
      }))
      .filter((c) => c.value !== "");

    setAppliedFilters(conditions);
    setPage(0);
    applyDynamicFilters(conditions, filterMatchType, 0, rowsPerPage);
  };

  // Wipes every column's box and whatever was applied, in one shot.
  const handleClearAllFilters = () => {
    setInlineFilterValues({});
    setDraftValues({});
    setAppliedFilters([]);
    setPage(0);
    applyDynamicFilters([], filterMatchType, 0, rowsPerPage);
  };

  // -----------------------------------------------------------------------
  // COMMON FILTER PANEL — handlers
  // -----------------------------------------------------------------------

  // The Filter button toggles the panel. When opening, the panel is
  // pre-filled with whatever is currently applied so the person always
  // sees what is live.
  const openFilterPanel = () => {
    if (filterPanelOpen) {
      setFilterPanelOpen(false);
      return;
    }
    const draft = {};
    appliedFilters.forEach((c) => {
      draft[c.field] = c.value;
    });
    setDraftValues(draft);
    setFilterPanelOpen(true);
  };

  // Closing without pressing "Apply Filters" discards unapplied edits.
  const closeFilterPanel = () => setFilterPanelOpen(false);

  const toggleAdvancedFilters = () => setAdvancedFiltersOpen((prev) => !prev);

  // Chip cross: removes ONE applied filter and reloads the table.
  const removeAppliedFilter = (field) => {
    const conditions = appliedFilters.filter((c) => c.field !== field);
    setAppliedFilters(conditions);
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setInlineFilterValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setPage(0);
    applyDynamicFilters(conditions, filterMatchType, 0, rowsPerPage);
  };

  const handleDraftValueChange = (key) => (event) => {
    const value = event.target.value;
    setDraftValues((prev) => ({ ...prev, [key]: value }));
  };

  // Excel copy-paste brings multiple values separated by newlines (rows) or
  // tabs (columns), with no commas. Convert that shape into the same
  // comma-separated "is any of" format, appending to whatever is typed.
  const handleFilterValuePaste = (key) => (e) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted || !/[\r\n\t]/.test(pasted)) return; // single value — default paste

    e.preventDefault();
    const joined = pasted
      .split(/[\r\n\t]+/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .join(", ");
    if (!joined) return;

    setDraftValues((prev) => {
      const current = (prev[key] || "").replace(/\s+$/, "");
      const next = !current
        ? joined
        : current.endsWith(",")
        ? `${current} ${joined}`
        : `${current}, ${joined}`;
      return { ...prev, [key]: next };
    });
  };

  // "Apply Filters" — every non-empty input becomes a condition, and the
  // whole set is sent in ONE api call. The panel closes afterwards.
  const applyFilterFromPanel = () => {
    const conditions = [...getBasicFilterColumns(), ...getAdvancedFilterColumns()]
      .map((c) => ({ field: c.key, value: (draftValues[c.key] || "").trim() }))
      .filter((c) => c.value !== "");

    setAppliedFilters(conditions);
    setFilterPanelOpen(false);
    setPage(0);
    applyDynamicFilters(conditions, filterMatchType, 0, rowsPerPage);
  };

  // Enter inside any input applies the filter.
  const handleFilterKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyFilterFromPanel();
    }
  };

  // table pagination fns
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setStateForPage(true);
    // getCreditNoteDetails();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    setStateForRowPerPage(true);
    // getCreditNoteDetails();
  };

  const handleErrorPageChange = (event, newPage) => {
    // newPage is zero-based from TablePagination, convert to one-based for API
    const adjustedPage = newPage + 1;
    setCurrentErrorPage(newPage); // Update UI page (zero-based)
    uploadReport(uploadResData?.records?.id || localStorage.getItem('uploadReportId'), adjustedPage, errorRowsPerPage);
  };
  const handleErrorRowsPerPageChange = (event) => {
    const newRowsPerPage = +event.target.value;
    setErrorRowsPerPage(newRowsPerPage);
    setCurrentErrorPage(0);
    uploadReport(uploadResData.records.id, 1, newRowsPerPage);
  };

  // table serach
  const SearchInputHandler = (event) => {
    setTableSearch(event.target.value);
    setPage(0);
    setRowsPerPage(25);
    setStateTableSearch(true);
  };

  useEffect(() => {
    if (stateTableSearch) {
      if (appliedFilters.length > 0) {
        applyDynamicFilters(appliedFilters, filterMatchType, 0, rowsPerPage);
      } else {
        getCreditNoteDetails(tableSearch);
      }
    }
    setStateTableSearch(false);
  }, [tableSearch]);

  // upload history table data api fn call
  // const getUploadHistoryDataApi = () => {
  //   axios
  //     .get(
  //       `/arc/get_data_upload_history/${localStorage.getItem(
  //         "business_entity_id"
  //       )}/${13}`
  //     )
  //     .then((res) => {
  //       setUploadHistoryTableData(res.data.record);
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //       // alert("Unable to complete your request, Please retry!");
  //     });
  // };


  const getUploadHistoryDataApi = () => {
    axios
      .get(
        `/arc/get_data_upload_history/${localStorage.getItem(
          "business_entity_id"
        )}/${13}`
      )
      .then((res) => {
        // Set the upload history table data
        setUploadHistoryTableData(res.data.record);

        // Extract the IDs from the response and store in localStorage
        const ids = res.data.record.map((item) => item.id);
        localStorage.setItem("upload_ids", JSON.stringify(ids));
      })
      .catch((error) => {
        console.log(error);
        // alert("Unable to complete your request, Please retry!");
      });
  };

  useEffect(() => {
    getUploadHistoryDataApi();
    getCreditNoteDetails();
  }, []);

  const fileUploader = (event) => {
    const file_data = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file_data);

    reader.onload = function () {
      let file_value = reader.result;
      axios
        .post(`/vendor/vendorcreditnote`, {
          file: file_value,
          org_code: localStorage.getItem("business_entity_id"),
        })
        .then((data) => {
          if (!data || !data.data) {
            return;
          }

          getUploadHistoryDataApi();
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message;
          if (errorMessage) {
            toast.error(errorMessage, { autoClose: 5000 });
          }
        });
    };
  };
  const closeUploadBoxWapper = () => {
    setUploadSection(false);
    getCreditNoteDetails();
  };

  const addFormHandler = () => {
    setOpenAddFormDrawer(true);
  };

  const editDataHandler = (item) => {
    delete item.vendorCode;
    delete item.orgCode;
    setEditData(item);
    setOpenEditDrawer(true);
  };

  const showDetailsHandler = (item) => {
    setShowData(item);
    setOpenShowDrawer(true);
  };

  const closeDrawer = () => {
    setOpenAddFormDrawer(false);
    setOpenEditDrawer(false);
    setOpenShowDrawer(false);
  };

  //excel download
  const downloadBase64File = (contentType, base64Data, fileName) => {
    const linkSource = `data:${contentType};base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const exportToExcel = () => {
    const contentType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = `vendor_credit_note.xlsx`;
    let Datas = {
      data: tableData,
      business_entity_id: localStorage.getItem("business_entity_id"),
    }
    if (fromPeriod && toPeriod) {
      Datas['from_period'] = moment(fromPeriod).format("YYYY-MM-DD")
      Datas['to_period'] = moment(toPeriod).format("YYYY-MM-DD")
    }
    axios
      .post("vendor/vendorcreditnotedownload", Datas)
      .then((res) => {
        downloadBase64File(contentType, res.data.file, filename);
      })
      .catch((error) => {
        console.log(error);
      });
  };



  // True while Credit Note filters are applied — drives the Export All
  // button's label ("Export Filtered") and what it exports.
  const hasAppliedFilters = appliedFilters.length > 0;

  // The credit note download needs every field present, takes dates only as
  // YYYY-MM-DD (empty -> null) and calls the vendor code `vendor_code`.
  const CREDIT_EXPORT_DATE_KEYS = ["originalBillDate", "bookingDate", "creditNoteDate", "dueDate"];
  const CREDIT_EXPORT_KEYS = [
    "vendorGstin", "erpVendorCode", "vendorPan", "entityGstin", "branchName",
    "vendorName", "originalBillNumber", "creditNoteNumber", "eInvoice", "hsnSacCode",
    "taxableValue", "nonTaxableValue", "sgst", "cgst", "igst", "cess", "taxAmount",
    "tcsSection", "tcsPayable", "totalAmount", "tdsSection", "tdsPayable",
    "expenseType", "taxCreditType", "itcEligibility", "rcmApplicability", "poNumber",
    "poDate", "dispute", "disputeValue", "boeNumber", "boeDate", "referenceDate",
    "portCode", "creditNoteReason", "billType", "purchaseType", "voucherId", "taxRate",
    "percentageTaxRate", "totalLineItem", "itemName", "itemCode", "uom", "quantity",
    "ecomGstin", "pos",
  ];
  const toCreditExportRecords = (records) =>
    (records || []).map((r) => {
      const row = { vendor_code: r.vendorCode ?? r.vendor_code ?? null };
      CREDIT_EXPORT_KEYS.forEach((k) => {
        row[k] = r[k] ?? null;
      });
      CREDIT_EXPORT_DATE_KEYS.forEach((k) => {
        const v = r[k];
        if (!v) {
          row[k] = null;
          return;
        }
        const m = /^(\d{2})-(\d{2})-(\d{4})/.exec(String(v));
        row[k] = m ? `${m[3]}-${m[2]}-${m[1]}` : v;
      });
      return row;
    });

  // Export All / Export Filtered (one button). No filters applied -> every
  // record, same request the existing Export sends with no period. Filters
  // applied -> only the records matching them: fetched from the existing
  // filter api (one page as big as the filtered result) and sent to the same
  // download api in `records`.
  const exportAllOrFiltered = () => {
    const contentType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = `vendor_credit_note.xlsx`;
    const businessEntityId = localStorage.getItem("business_entity_id");

    setIsExportingAll(true);
    const request = hasAppliedFilters
      ? axios
          .post(`/get_vendor_credit_detailsFilter`, {
            business_entity_id: businessEntityId,
            search_text: tableSearch || "",
            filters: {
              match_type: filterMatchType,
              conditions: appliedFilters.map((c) => ({
                field: c.field,
                operator: "in",
                value: parseBulkFilterValues(c.value),
              })),
            },
            number_of_rows_per_page: Math.max(rowCount || 0, 1),
            page_number: 0,
          })
          .then((res) => {
            const records = toCreditExportRecords(res?.data?.record || res?.data?.records);
            if (records.length === 0) {
              toast.error("No records found for the selected filters", { autoClose: 2000 });
              return null;
            }
            return axios.post("vendor/vendorcreditnotedownload", {
              business_entity_id: businessEntityId,
              records: records,
            });
          })
      : axios.post("vendor/vendorcreditnotedownload", {
          business_entity_id: businessEntityId,
        });

    request
      .then((res) => {
        if (res) downloadBase64File(contentType, res.data.file, filename);
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          hasAppliedFilters ? "Failed to export filtered data" : "Failed to export all data",
          { autoClose: 2000 }
        );
      })
      .finally(() => {
        setIsExportingAll(false);
      });
  };

  const uploadReport = (id, pageParam = errorPage, perPage = perErrorPages) => {
    // Use provided id or retrieve from localStorage
    let currentId = id || localStorage.getItem('uploadReportId');

    // If no id is available, set error message and exit
    if (!currentId) {
      setUploadErrorMsg('Invalid ID');
      return;
    }

    // Store id in localStorage for future calls
    localStorage.setItem('uploadReportId', currentId);

    axios
      .get(`/arc/get_data_upload_json/${currentId}/${pageParam}/${perPage}`)
      .then((res) => {
        setUploadSection(true);
        var jsonData = res.data.records;

        if (res.data.records.type === "message") {
          setUploadResData();
          setUploadErrorMsg(res.data.records.message);
        } else {
          setUploadErrorMsg();
          getCreditNoteDetails();
          setUploadResData(jsonData);
        }
      })
      .catch((error) => { });
  };

  // const uploadReport = (id) => {
  //   axios
  //     .get(`/arc/get_data_upload_json/${id}`)
  //     .then((res) => {
  //       setUploadSection(true);
  //       var jsonData = res.data.records;

  //       if (res.data.records.type === "message") {
  //         setUploadResData();
  //         setUploadErrorMsg(res.data.records.message);
  //       } else {
  //         setUploadErrorMsg();
  //         getCreditNoteDetails();
  //         setUploadResData(jsonData);
  //       }
  //     })
  //     .catch((error) => { });
  // };

  // refresh Upload History Table using refresh button
  const refreshUploadHistoryTable = () => {
    getUploadHistoryDataApi();
    getCreditNoteDetails();
  };


  const errorLogExportHandlerForUpload = () => {
    setLoadingExport(true);
    // Get the currentId from localStorage
    let uploadId = localStorage.getItem('uploadReportId');

    let data = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      upload_id: uploadId,
    };

    const contentType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = `vendorCreditNoteUploadErrorLog.xlsx`;

    axios
      .post(`/uploads_errors_extract/download`, data)
      .then((res) => {
        downloadBase64File(contentType, res.data.file, filename);
        setLoadingExport(false);
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        setLoadingExport(false);
      });

  };

  // const errorLogExportHandlerForUpload = () => {
  //   let data = {
  //     business_entity_id: localStorage.getItem("business_entity_id"),
  //     records: uploadResData,
  //   };
  //   const contentType =
  //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  //   const filename = `vendorCreditNoteUploadErrorLog.xlsx`;

  //   axios
  //     .post(`/uploads_errors_extract/download`, data)
  //     .then((res) => {
  //       downloadBase64File(contentType, res.data.file, filename);
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //     });
  // };

  const handleOpenAndCloseModel = () => {
    setTriggerModelFromToDate(!triggerModelFromToDate)
  };

  const inputHandlerFromDatePicker = (v) => {
    setFromPeriodDisplay(v)
    setFromPeriod(v)
  };

  const inputHandlerToDatePicker = (v) => {
    setToPeriodDisplay(v)
    setToPeriod(v)
  };

  const onSubmitHandlerForDateFilter = () => {

    exportToExcel()

    setDateFilterOption("withDateFilter")
    setTriggerModelFromToDate(false)
    setFromPeriodDisplay(null)
    setFromPeriod(null)
    setToPeriodDisplay(null)
    setToPeriod(null)


  }

  const renderDateFilter = () => {
    return (
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={triggerModelFromToDate}
        onHide={handleOpenAndCloseModel}
        dialogClassName="modal-50w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Export Credit Note</Modal.Title>
        </Modal.Header>
        <ValidatorForm
          useRef="form"
          onSubmit={onSubmitHandlerForDateFilter}
          onError={(errors) => console.log(errors)}
        >
          <Modal.Body>
            <Grid container spacing={3}>
              <Grid item sm={12}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="dateFilterOption"
                    value={dateFilterOption}
                    onChange={(e) => setDateFilterOption(e.target.value)}
                  >
                    <FormControlLabel
                      value="withDateFilter"
                      control={<Radio />}
                      label="With Date Filter"
                    />
                    <FormControlLabel
                      value="withoutDateFilter"
                      control={<Radio />}
                      label="Without Date Filter"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {dateFilterOption === "withDateFilter" && (
                <>
                  <Grid item sm={2}>
                    <p style={{ marginTop: "10px", fontSize: "16px" }}>
                      From Date<span style={{ color: "red" }}>*</span>:
                    </p>
                  </Grid>
                  <Grid item sm={4}>
                    <DatePicker
                      maxDate={toPeriodDisplay ?? ""}
                      placeholder="From Date"
                      errorMessages={["This field is required"]}
                      validators={["required"]}
                      value={fromPeriodDisplay ?? ""}
                      onChange={(v) => inputHandlerFromDatePicker(v)}
                    />
                  </Grid>

                  <Grid item sm={2}>
                    <p style={{ marginTop: "10px", fontSize: "16px" }}>
                      To Date<span style={{ color: "red" }}>*</span>:
                    </p>
                  </Grid>
                  <Grid item sm={4}>
                    <DatePicker
                      minDateForDatepicker={fromPeriodDisplay ?? ""}
                      placeholder="To Date"
                      errorMessages={["This field is required"]}
                      validators={["required"]}
                      value={toPeriodDisplay ?? ""}
                      onChange={(v) => inputHandlerToDatePicker(v)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Modal.Body>
          <Modal.Footer>
            <button className="button-primary" type="submit">
              Export
            </button>
            <button
              className="button-outlined-secondary"
              type="button"
              onClick={handleOpenAndCloseModel}
            >
              Cancel
            </button>
          </Modal.Footer>
        </ValidatorForm>
      </Modal>
    );
  };

  // -----------------------------------------------------------------------
  // IN-GRID FILTER — rendering
  // -----------------------------------------------------------------------
  // The slim bar directly above the table. Returns null when there is
  // nothing to show (no applied filters, nothing staged) so it never adds
  // visual weight to the page until it's actually useful.
  const renderFilterActionBar = () => {
    const stagedCount = stagedFilterFieldCount();
    const hasStaged = hasStagedFilterChanges();
    const hasApplied = appliedFilters.length > 0;

    if (!hasStaged && !hasApplied) return null;

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          padding: "7px 14px",
          marginBottom: "6px",
          borderRadius: "8px",
          background: hasStaged
            ? `linear-gradient(90deg, ${ACCENT}, #115e59)`
            : "#f1f5f9",
          color: hasStaged ? "#ffffff" : "#334155",
          transition: "background 0.2s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12.5px" }}>
          <TuneIcon style={{ fontSize: "16px" }} />
          <span>
            {hasStaged
              ? `${stagedCount} field${stagedCount === 1 ? "" : "s"} ready to filter`
              : `${appliedFilters.length} filter${
                  appliedFilters.length === 1 ? "" : "s"
                } active`}
          </span>
        </div>

        <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
          {hasApplied && (
            <button
              type="button"
              onClick={handleClearAllFilters}
              style={{
                background: "transparent",
                border: "none",
                fontSize: "12px",
                cursor: "pointer",
                color: hasStaged ? "rgba(255,255,255,0.9)" : ACCENT,
                textDecoration: "underline",
              }}
            >
              Clear all
            </button>
          )}
          {hasStaged && (
            <button
              type="button"
              onClick={applyInlineFilters}
              style={{
                background: "#ffffff",
                color: ACCENT,
                border: "none",
                borderRadius: "999px",
                padding: "4px 18px",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
              }}
            >
              Apply Filters
            </button>
          )}
        </div>
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // COMMON FILTER PANEL — rendering
  // -----------------------------------------------------------------------
  const renderFilterButton = () => {
    const appliedCount = appliedFilters.length;
    return (
      <button
        type="button"
        className="button-outlined-primary"
        onClick={openFilterPanel}
        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
      >
        <FilterAltOutlinedIcon style={{ fontSize: "18px" }} />
        {appliedCount > 0 ? `Filter (${appliedCount})` : "Filter"}
      </button>
    );
  };

  // One label + white input (dropdown for the columns in FILTER_DROPDOWN_OPTIONS).
  const renderFilterField = (col) => {
    const common = {
      fullWidth: true,
      size: "small",
      variant: "outlined",
      value: draftValues[col.key] ?? "",
      onChange: handleDraftValueChange(col.key),
      InputProps: { style: { background: "#fff" } },
    };
    const options = FILTER_DROPDOWN_OPTIONS[col.key];

    return (
      <div key={col.key} style={{ minWidth: 0 }}>
        <label style={{ display: "block", fontSize: "12px", marginBottom: "4px" }}>
          {col.label}
        </label>
        {FILTER_DATE_KEYS.includes(col.key) ? (
          <ValidatorForm onSubmit={() => {}} style={{ margin: 0 }}>
            <DatePicker
              fullWidth
              name={`filter_${col.key}`}
              placeholder="Select date"
              value={draftValues[col.key] ? moment(draftValues[col.key], "YYYY-MM-DD").toDate() : ""}
              onChange={(v) =>
                handleDraftValueChange(col.key)({
                  target: { value: v && moment(v).isValid() ? moment(v).format("YYYY-MM-DD") : "" },
                })
              }
            />
          </ValidatorForm>
        ) : options ? (
          <TextField {...common} select SelectProps={{ native: true }}>
            <option value="">Select</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </TextField>
        ) : (
          <TextField
            {...common}
            placeholder={`Enter ${col.label.toLowerCase()}`}
            onPaste={handleFilterValuePaste(col.key)}
            onKeyDown={handleFilterKeyDown}
          />
        )}
      </div>
    );
  };

  const renderFilterPanel = () => {
    if (!filterPanelOpen) return null;

    const advancedColumns = getAdvancedFilterColumns();
    const advancedCount = advancedColumns.filter(
      (c) => (draftValues[c.key] || "").trim() !== ""
    ).length;

    const gridStyle = {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "12px",
    };

    return (
      <div
        style={{
          width: 440,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          minHeight: 360,
          // Keeps the panel table-sized; the body scrolls inside.
          maxHeight: "calc(100vh - 260px)",
          border: `1px solid ${HEADER_TEXT}`,
          borderRadius: 4,
          background: "#fff",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(3,64,111,0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 6px 6px 14px",
            background: HEADER_TEXT,
            color: "#ffffff",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 600 }}>Filters</span>
          <Tooltip title="Close" arrow>
            <IconButton size="small" onClick={closeFilterPanel}>
              <ClearIcon style={{ fontSize: "16px", color: "#ffffff" }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Body: Basic (open) + Advanced (collapsed) */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "12px 14px",
            background: "#eef3f8",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: HEADER_TEXT,
              marginBottom: "10px",
            }}
          >
            Basic Filters
          </div>
          <div style={gridStyle}>
            {getBasicFilterColumns().map((col) => renderFilterField(col))}
          </div>

          <div
            role="button"
            onClick={toggleAdvancedFilters}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              marginTop: "14px",
              padding: "10px 0",
              borderTop: `1px solid ${HEADER_BORDER}`,
              fontSize: "12px",
              fontWeight: 600,
              color: HEADER_TEXT,
              userSelect: "none",
            }}
          >
            <span>
              {advancedCount > 0
                ? `Advanced Filters (${advancedCount})`
                : "Advanced Filters"}
            </span>
            {advancedFiltersOpen ? (
              <ExpandLessIcon style={{ fontSize: "20px" }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: "20px" }} />
            )}
          </div>
          {advancedFiltersOpen && (
            <div style={gridStyle}>
              {advancedColumns.map((col) => renderFilterField(col))}
            </div>
          )}
        </div>

        {/* Footer — bottom right */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "10px 14px",
            background: HEADER_GROUP_BG,
            borderTop: `1px solid ${HEADER_BORDER}`,
          }}
        >
          <button
            type="button"
            className="button-outlined-secondary"
            onClick={handleClearAllFilters}
          >
            Clear All
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={applyFilterFromPanel}
          >
            Apply Filters
          </button>
        </div>
      </div>
    );
  };

  // Applied filters as small chips, each with its own cross.
  const renderAppliedFilterChips = () => {
    if (appliedFilters.length === 0) return null;

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginBottom: "8px",
        }}
      >
        {appliedFilters.map((c) => (
          <Chip
            key={c.field}
            size="small"
            variant="outlined"
            label={`${getFieldMeta(c.field).label}: ${FILTER_DATE_KEYS.includes(c.field) ? toDisplayDate(c.value) : c.value}`}
            onDelete={() => removeAppliedFilter(c.field)}
            style={{
              maxWidth: 280,
              backgroundColor: HEADER_GROUP_BG,
              color: HEADER_TEXT,
              borderColor: HEADER_BORDER,
            }}
          />
        ))}
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // GROUPED HEADER — returns an array of TWO <tr> elements:
  //   1) the colored group-band row, each band spanning all of its columns
  //      via colSpan and sized to the total width of its columns, plus the
  //      trailing SHOW cell which spans both header rows
  //   2) the column-label row
  // The caller drops the array straight into <tbody> (NOT inside another
  // <tr>). Both rows have fixed heights so the sticky offsets of the label
  // row and the filter row stack exactly.
  // -----------------------------------------------------------------------
  const renderCreditNoteTableHeader = () => {
    const groups = getColumnGroups();

    const groupRow = (
      <tr key="group-row">
        {groups.map((g) => {
          const width = getGroupWidth(g.name);
          return (
            <th
              key={g.name}
              colSpan={g.span}
              style={{
                position: "sticky",
                top: 0,
                zIndex: 510,
                height: GROUP_ROW_HEIGHT,
                lineHeight: "18px",
                width: width,
                minWidth: width,
                maxWidth: width,
                padding: "6px 8px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                color: HEADER_TEXT,
                background: HEADER_GROUP_BG,
                textAlign: "center",
                borderRight: `1px solid ${HEADER_BORDER}`,
                borderBottom: `1px solid ${HEADER_BORDER}`,
                whiteSpace: "nowrap",
                boxSizing: "border-box",
              }}
            >
              {g.name}
            </th>
          );
        })}
        <th
          rowSpan={2}
          style={{
            position: "sticky",
            top: 0,
            right: 0,
            zIndex: 520,
            width: SHOW_COL_WIDTH,
            minWidth: SHOW_COL_WIDTH,
            maxWidth: SHOW_COL_WIDTH,
            backgroundColor: HEADER_GROUP_BG,
            color: HEADER_TEXT,
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            textAlign: "center",
            verticalAlign: "middle",
            borderLeft: `1px solid ${HEADER_BORDER}`,
            borderBottom: `1px solid ${HEADER_BORDER}`,
            boxShadow: "-2px 0 4px rgba(0,0,0,0.06)",
            boxSizing: "border-box",
          }}
        >
          SHOW
        </th>
      </tr>
    );

    const labelRow = (
      <tr key="label-row">
        {allColumns.map((col) => (
          <th
            key={col.key}
            title={col.label}
            style={{
              position: "sticky",
              top: GROUP_ROW_HEIGHT,
              zIndex: 500,
              height: LABEL_ROW_HEIGHT,
              lineHeight: "18px",
              width: col.width,
              minWidth: col.width,
              maxWidth: col.width,
              backgroundColor: HEADER_LABEL_BG,
              padding: "6px 8px",
              fontSize: "11.5px",
              fontWeight: 600,
              color: "#334155",
              textAlign: "center",
              borderRight: `1px solid ${HEADER_BORDER}`,
              borderBottom: `1px solid ${HEADER_BORDER}`,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              boxSizing: "border-box",
            }}
          >
            {col.label.toUpperCase()}
          </th>
        ))}
      </tr>
    );

    return [groupRow, labelRow];
  };

  // The filter row itself — lives as a `<tr>` right under the label row,
  // inside the same table, one comma-separated box per filterable column.
  // Pressing Enter in any box applies every staged box at once. The
  // trailing display-only date columns have no filter, so they get plain
  // empty cells, and the cell under SHOW is pinned to the right edge.
  const renderInlineFilterRow = () => {
    const filterRowTop = GROUP_ROW_HEIGHT + LABEL_ROW_HEIGHT;
    const appliedByField = Object.fromEntries(
      appliedFilters.map((c) => [c.field, c.value])
    );

    return (
      <tr>
        {filterableColumns.map((col) => {
          const value = getInlineFilterValue(col.key);
          const isApplied =
            value.trim() !== "" && (appliedByField[col.key] ?? "").trim() === value.trim();

          return (
            <td
              key={col.key}
              style={{
                position: "sticky",
                top: filterRowTop,
                zIndex: 400,
                width: col.width,
                minWidth: col.width,
                maxWidth: col.width,
                padding: "6px 10px",
                background: "#f0fdfa",
                borderBottom: `2px solid ${isApplied ? ACCENT : "#d9e6e4"}`,
                boxSizing: "border-box",
              }}
            >
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleInlineFilterChange(col.key, e.target.value)}
                  onPaste={(e) => {
                    // Excel copy-paste brings multiple values separated by
                    // newlines (one per row) or tabs (one per column), with
                    // no commas at all. Detect that shape and convert it
                    // into the same comma-separated "is any of" format this
                    // box expects, appending to whatever is already typed.
                    const pasted = e.clipboardData.getData("text");
                    if (!pasted || !/[\r\n\t]/.test(pasted)) return; // plain single-value paste — let default happen

                    e.preventDefault();
                    const pastedValues = pasted
                      .split(/[\r\n\t]+/)
                      .map((v) => v.trim())
                      .filter((v) => v.length > 0);
                    if (pastedValues.length === 0) return;

                    const joined = pastedValues.join(", ");
                    const current = value.replace(/\s+$/, "");
                    const newValue =
                      current === ""
                        ? joined
                        : current.endsWith(",")
                        ? `${current} ${joined}`
                        : `${current}, ${joined}`;

                    handleInlineFilterChange(col.key, newValue);
                  }}
                  onKeyDown={(e) => {
                    // Enter applies every staged box at once — same effect
                    // as clicking "Apply Filters" in the bar above.
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyInlineFilters();
                    }
                  }}
                  placeholder={`e.g. ${col.label.toLowerCase()} 1, 2`}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    fontSize: "12px",
                    fontFamily: "inherit",
                    padding: "3px 18px 3px 2px",
                    color: "#0f172a",
                    textAlign: "center",
                  }}
                />
                {value && (
                  <ClearIcon
                    onClick={() => clearInlineFilterField(col.key)}
                    style={{
                      position: "absolute",
                      right: 0,
                      fontSize: "14px",
                      color: "#94a3b8",
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>
            </td>
          );
        })}
        {nonFilterableColumns.map((col) => (
          <td
            key={`${col.key}_filter`}
            style={{
              position: "sticky",
              top: filterRowTop,
              zIndex: 400,
              width: col.width,
              minWidth: col.width,
              maxWidth: col.width,
              background: "#f0fdfa",
              borderBottom: "2px solid #d9e6e4",
              boxSizing: "border-box",
            }}
          />
        ))}
        <td
          style={{
            position: "sticky",
            top: filterRowTop,
            right: 0,
            zIndex: 410,
            width: SHOW_COL_WIDTH,
            minWidth: SHOW_COL_WIDTH,
            maxWidth: SHOW_COL_WIDTH,
            padding: "6px 10px",
            background: "#f0fdfa",
            borderBottom: "2px solid #d9e6e4",
            textAlign: "center",
            boxShadow: "-2px 0 4px rgba(0,0,0,0.08)",
            boxSizing: "border-box",
          }}
        >
          {appliedFilters.length > 0 && (
            <Tooltip title="Clear all filters">
              <IconButton size="small" onClick={handleClearAllFilters}>
                <ClearIcon style={{ fontSize: "16px", color: ACCENT }} />
              </IconButton>
            </Tooltip>
          )}
        </td>
      </tr>
    );
  };

  // Every cell renders as-is (or through the column's own `render`, e.g.
  // amounts), falling back to "-" when empty, so adding or reordering a
  // column in `filterableColumns` / `nonFilterableColumns` is enough to
  // update the header, filter row and body together.
  const renderCellValue = (col, item) => {
    const raw = item[col.key];
    if (raw === null || raw === undefined || raw === "") return "-";
    return col.render ? col.render(item) : raw;
  };

  return (
    <div>
      {/* Back arrow */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <Tooltip title="Back" arrow>
          <IconButton
            onClick={() => props.history.push('/Data-Upload')}
            sx={{
              borderRadius: '50%',
              padding: '6px',
              color: '#263238',
              fontSize: '20px',
              backgroundColor: '#ffffff',
              height: '36px',
              width: '36px',
              '&:hover': {
                backgroundColor: '#455a64',
                color: '#ffffff',
              },
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              transition: 'background-color 0.3s ease',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {(localStorage.getItem('is_uploads') !== "N") && <>
        {/* <div>
          <ToggleSwitch
            onChange={() => setShowUploadHistoryTable(!showUploadHistoryTable)}
            value={showUploadHistoryTable}
            label={`Upload Credit Note`}
          />
        </div> */}

        {(localStorage.getItem('is_uploads') !== "N") && (
          <BoxWrapper boxTitle="Upload Credit Note History">
            <a
              href={VendorCreditNote}
              download={"vendor_credit_note.xlsx"}
              style={{ textDecoration: "none" }}
            >
              {" "}
              <Button component="label" size="small" color="primary">
                <GetAppIcon style={{ fontSize: "20px" }} />
                DOWNLOAD TEMPLATE
              </Button>
            </a>
            {canWrite("UPLOADING PURCHASE") && (
            <Button component="label" size="small" color="primary">
              <PublishIcon style={{ fontSize: "20px" }} />
              UPLOAD TEMPLATE
              <input
                accept=".xlsx"
                name="file"
                type="file"
                value=""
                hidden
                onChange={fileUploader}
              />
            </Button>
            )}
            <Button
              component="label"
              size="small"
              color="primary"
              style={{ float: "right" }}
              onClick={refreshUploadHistoryTable}
            >
              <RefreshIcon style={{ fontSize: "20px" }} />
              Refresh
            </Button>

            <div className="responsive-table">
              <table className="custome-table">
                <thead>
                  <tr>
                    <th>Last Upload Date & Time</th>
                    <th>Status</th>
                    <th>Upload Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadHistoryTableData.length > 0 &&
                    uploadHistoryTableData.map((item, i) => {
                      return (
                        <tr>
                          <td>{item.upload_date}</td>
                          <td>{item.status}</td>
                          <td>{item.upload_type}</td>
                          <td>
                            {item.status === "In Progress" ? (
                              <RemoveRedEyeOutlinedIcon color="disabled" />
                            ) : item.status === "Uploaded" ? (
                              <RemoveRedEyeOutlinedIcon color="disabled" />
                            ) : (
                              <RemoveRedEyeOutlinedIcon
                                className="action-icon-button showIcon"
                                onClick={() => uploadReport(item.id)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {uploadHistoryTableData.length === 0 && (
                    <tr>
                      <td
                        style={{ color: "red", textAlign: "center" }}
                        colspan={4}
                      >
                        No Data Found!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </BoxWrapper>
        )}
      </>}

      {uploadSection && (
        <BoxWrapper
          boxTitle="Upload Details"
          cutIcon={uploadSection}
          closeBoxWapper={closeUploadBoxWapper}
          enableAddButton={
            uploadResData && uploadResData.records.summary.invalid_records > 0
              ? true
              : null
          }
          addButtonLabel={loadingExport ? "LOADING..." : "EXPORT"}
          addButtonHandlerFn={errorLogExportHandlerForUpload}
          loadingExport={loadingExport}
        >
          {uploadErrorMsg && (
            <div style={{ marginBottom: "20px", color: "red" }}>
              {uploadErrorMsg}
            </div>
          )}

          {uploadResData && (
            <>
              <div>
                <Grid container spacing={3}>
                  <Grid item sm={4}>
                    <Card
                      backgroundcolor="#6f9eaf"
                      cardTitle="Total Records"
                      cardBody={uploadResData.records.summary.total_records}
                    />
                  </Grid>
                  <Grid item sm={4}>
                    <Card
                      backgroundcolor="#007965"
                      cardTitle="Successful Records"
                      cardBody={uploadResData.records.summary.valid_records}
                    />
                  </Grid>
                  <Grid item sm={4}>
                    <Card
                      backgroundcolor="#eb596e"
                      cardTitle="Error Records"
                      cardBody={uploadResData.records.summary.invalid_records}
                    />
                  </Grid>
                </Grid>
              </div>
              <div className="upload-details-table-section">
                <div className="tableResponisve">
                  {uploadResData.status === false && (
                    <table className="tableCss">
                      <thead>
                        <tr>
                          <th>GSTIN</th>
                          <th>Row #</th>
                          <th>Status</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResData.status === false &&
                          uploadResData.records.details.map((item, i, arr) => {
                            if (item.status === "Fail") {
                              return (
                                <tr>
                                  <td>{item.record.vendorGstin}</td>
                                  <td>{item.row}</td>
                                  <td>{item.status}</td>
                                  <td style={{ color: "red" }}>
                                    {Object.keys(item.errors_in).map(
                                      (v, i, ar) => {
                                        if (ar.length > 1) {
                                          return (
                                            <div>
                                              <span style={{ color: "black" }}>
                                                {v}
                                              </span>{" "}
                                              : {item.errors_in[v].join(",")}
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <div>
                                              <span style={{ color: "black" }}>
                                                {v}
                                              </span>{" "}
                                              : {item.errors_in[v].join(",")}
                                            </div>
                                          );
                                        }
                                      }
                                    )}
                                  </td>
                                </tr>
                              );
                            }
                          })}
                      </tbody>
                    </table>
                  )}
                </div>
                <TablePagination
                  rowsPerPageOptions={[25, 100, 500, 1000, 3000]}
                  component="div"
                  count={uploadResData?.records?.summary?.invalid_records || errorTotal || 0} // Ensure count is valid
                  rowsPerPage={errorRowsPerPage}
                  page={currentErrorPage}
                  onPageChange={handleErrorPageChange}
                  onRowsPerPageChange={handleErrorRowsPerPageChange}
                />

              </div>
            </>
          )}
        </BoxWrapper>
      )}

      {/* credit details table */}
      <BoxWrapper
        boxTitle="Credit Note Details"
        exportToExcelEnable={true}
        exportToExcelFn={handleOpenAndCloseModel}
        exportToExcelEnable1={true}
        exportButtonLabel1={hasAppliedFilters ? "Export Filtered" : "Export All"}
        exportToExcelFn1={exportAllOrFiltered}
        loadingexcel1={isExportingAll}
        // search input
        // enableSearchInput={true}
        // searchInputPlaceholder="Search..."
        // searchInputOnchange={SearchInputHandler}
        // Filter button inside the BoxWrapper header (see
        // FILTER_BUTTON_IN_BOXWRAPPER at the top of this file)
        headerExtra={FILTER_BUTTON_IN_BOXWRAPPER ? renderFilterButton() : null}
      >
        {/* Filter button here only until it is moved into BoxWrapper */}
        {!FILTER_BUTTON_IN_BOXWRAPPER && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "8px",
            }}
          >
            {renderFilterButton()}
          </div>
        )}

        {renderAppliedFilterChips()}

        {/* Table on the left, filter panel (when open) on the right —
            the panel stretches to the table's height. */}
        <div style={{ display: "flex", alignItems: "stretch", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>

        {/* invoice details table */}
        {/*
          overflowX: "auto" turns the wide, fixed-width grouped table below
          into a horizontally-scrolling region instead of letting the
          browser squeeze the columns to fit. The table's own minWidth is
          the sum of every column's fixed width plus the sticky SHOW column.
        */}
        <div
          className="responsive-table"
          style={{ overflowX: "auto", overflowY: "visible", maxWidth: "100%" }}
        >
          <table
            className="custome-table"
            style={{
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: getTableMinWidth(),
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            {/*
              colgroup pins every column to its real width. Without it,
              table-layout:fixed reads widths from the FIRST row (the
              group-band row, whose cells use colSpan), which splits a
              band's width evenly across its columns and pushes the labels,
              filter boxes and data out of line.
            */}
            <colgroup>
              {allColumns.map((col) => (
                <col key={col.key} style={{ width: col.width, minWidth: col.width }} />
              ))}
              <col style={{ width: SHOW_COL_WIDTH, minWidth: SHOW_COL_WIDTH }} />
            </colgroup>
            <tbody>
              {/* Grouped header: returns [groupRow, labelRow] — each is
                  already a <tr>, so no extra <tr> wrapper here. */}
              {renderCreditNoteTableHeader()}
              {/* In-grid filter row — one comma-separated box per column,
                  directly under the grouped header. */}
              {SHOW_INLINE_FILTER_ROW && renderInlineFilterRow()}

              {tableData.map((item, i, arr) => {
                return (
                  <tr key={i}>
                    {allColumns.map((col) => {
                      const cellValue = renderCellValue(col, item);
                      return (
                        <td
                          key={col.key}
                          title={String(cellValue)}
                          style={{
                            width: col.width,
                            minWidth: col.width,
                            maxWidth: col.width,
                            borderRight: "1px solid #f1f5f9",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            padding: "6px 10px",
                            boxSizing: "border-box",
                            textAlign: "center",
                          }}
                        >
                          {cellValue}
                        </td>
                      );
                    })}
                    {/* <td>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => editDataHandler(item)}
                        >
                          Edit
                        </Button>
                      </td> */}
                    <td
                      style={{
                        position: "sticky",
                        right: 0,
                        zIndex: 300,
                        width: SHOW_COL_WIDTH,
                        minWidth: SHOW_COL_WIDTH,
                        maxWidth: SHOW_COL_WIDTH,
                        background: "#fff",
                        textAlign: "center",
                        boxShadow: "-2px 0 4px rgba(0,0,0,0.08)",
                        boxSizing: "border-box",
                      }}
                    >
                      <RemoveRedEyeOutlinedIcon
                        className="action-icon-button showIcon"
                        onClick={() => showDetailsHandler(item)}
                      />
                    </td>
                  </tr>
                );
              })}

              {tableData.length === 0 && loading === false && (
                <tr>
                  <td
                    colSpan={allColumns.length + 1}
                    style={{ color: "red", textAlign: "center" }}
                  >
                    {appliedFilters.length > 0 ? (
                      <>
                        No records found for the selected filters.{" "}
                        <span
                          role="button"
                          onClick={handleClearAllFilters}
                          style={{ color: HEADER_TEXT, textDecoration: "underline", cursor: "pointer" }}
                        >
                          Clear filters
                        </span>
                      </>
                    ) : (
                      "No Data Found!"
                    )}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td
                    colSpan={allColumns.length + 1}
                    style={{
                      color: "black",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    Loading...Please wait
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          rowsPerPageOptions={[25, 100, 500, 1000, 3000]}
          component="div"
          count={rowCount} //total row count
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        </div>

        {renderFilterPanel()}
        </div>
      </BoxWrapper>

      {/* add Form drawer */}
      <Drawer
        anchor="right"
        open={openAddFormDrawer}
        onClose={() => setOpenAddFormDrawer(false)}
        variant={onCickOperation === true ? "persistent" : ""}
      >
        <div className="Drawer-title">Add Credit Note</div>
        <div className="drawer-body">
          <AddCreditNoteForm
            updateTableAfterSubmit={getCreditNoteDetails}
            closeAddFormDrawer={closeDrawer}
          />
        </div>
      </Drawer>

      {/* edit drawer */}
      <Drawer
        anchor="right"
        open={openEditDrawer}
        onClose={() => setOpenEditDrawer(false)}
      >
        <div className="Drawer-title">Edit Credit</div>
        <div className="drawer-body">
          <EditForm
            editData={editData}
            updateTableAfterSubmit={getCreditNoteDetails}
            closeEditFormDrawer={closeDrawer}
          />
        </div>
      </Drawer>

      {/* show drawer */}
      <RightDrawer
        drawerName="Credit Note Details"
        open={openShowDrawer}
        onClose={() => setOpenShowDrawer(false)}
      >
        <ShowCreditForm showData={showData} closeDrawer={closeDrawer} />
      </RightDrawer>
      {renderDateFilter()}
    </div>
  );
}

export default withRouter(InvoiceMgmt);